import {useNavigate, useParams} from 'react-router-dom';
import {useIntl} from 'react-intl';
import {useLang} from '../../../_metronic/i18n/Metronici18n';
import {DateTime} from 'luxon';
import {OverlayTrigger, ProgressBar, Tooltip} from 'react-bootstrap';
import {PageTitle} from '../../../_metronic/layout/core';
import Level3Title from '../../../app/common/components/title/Level3Title';
import EtatBadge from '../Components/EtatBadge';
import {getStatusColor, getStatusLabel} from '../Helpers/stateHelper';
import {useGetServiceInfo} from '../Hooks/ServiceManager';
import {useGetConsommation} from '../Hooks/ManageConsommation';
import {useGetGraph} from '../Hooks/MonitoringManager';
import {useGetRouterCapabilities} from '../../Router/Hooks/RouterManager';
import {DEFAULT_GRAPH_PERIOD_HOURS} from '../Types/graphPeriods';
import {EtatLien, ListService} from '../Types/service';
import {ReclamationReason, ReclamationTypes} from '../../Ticket/Type/reclamation';
import NotFoundRedirect from '../../../app/common/components/NotFoundRedirect';
import {isNotFoundError} from '../../../app/common/tools/httpError';

// Données factices : utilisées uniquement pour les champs sans source backend.
// TODO: à remplacer dès qu'un endpoint exposera ces informations techniques.
const donneesTechniquesFactices = {
  etatLien: 'CONNECTED' as EtatLien,
  vlan: '1234',
  sousReseauPublic: '91.121.142.16/29',
  passerelle: '91.121.142.17',
  dnsCeleste: '213.186.33.99 / .100',
  miseEnProduction: '12 mars 2024',
  popRaccordement: 'MRS — Saint-Charles',
  boitierMedia: 'MRS-STC-04 · port 14',
  sla: '99,9 %',
  ha4gActive: true,
  bannerSousTexte: 'Aucun incident · 100 % uptime ce mois',
  debit: '1 000 Mbit/s',
  ip: '91.121.142.18',
  traficDownload: '187 Mbit/s',
  traficUpload: '42 Mbit/s',
  latence: '4,2 ms',
  pertes: '0,01 %',
  uptimeMois: '100 %',
  routeur: {
    libelle: 'Cisco ISR 1100-4G — managé par Celeste',
    serial: 'FJC2542G0P3',
    mac: '00:1B:44:11:3A:B7',
    description: 'Modèle pré-configuré · supervision active 24/7',
    synchronise: true,
    firmware: 'IOS-XE 17.06.04a',
    ipManagement: '10.99.142.17',
    dernierCheckIn: 'il y a 2 minutes',
    interfaces: [
      {
        port: 'WAN0',
        mode: 'Fibre SFP',
        etatOk: true,
        etat: 'UP',
        debit: '1 Gbit/s · full-duplex',
        vlan: '—',
        description: 'Lien principal Celeste',
      },
      {
        port: 'WAN1',
        mode: 'LTE 4G',
        etatOk: true,
        etat: 'UP — secours',
        debit: '50 Mbit/s · signal -71 dBm',
        vlan: '—',
        description: 'Backup haute disponibilité',
      },
      {
        port: 'LAN1',
        mode: 'Ethernet',
        etatOk: true,
        etat: 'UP',
        debit: '1 Gbit/s · full-duplex',
        vlan: '10',
        description: 'Réseau bureautique',
      },
    ],
  },
};

interface InfoItemProps {
  label: string;
  value: string;
  factice?: boolean;
  fullWidth?: boolean;
}

const InfoItem = ({label, value, factice = false, fullWidth = false}: InfoItemProps): JSX.Element => (
  <div className={`${fullWidth ? 'col-12' : 'col-md-4'} mb-6`}>
    <div className="text-muted fs-7 mb-1">{label}</div>
    <div className={`fw-semibold fs-5 ${factice ? 'text-gray-500 fst-italic' : 'text-gray-800'}`}>
      {value}
    </div>
  </div>
);

interface StatBoxProps {
  label: string;
  value: string;
  factice?: boolean;
}

const StatBox = ({label, value, factice = false}: StatBoxProps): JSX.Element => (
  <div className="col-4">
    <div className="border border-gray-300 rounded p-4 h-100">
      <div className="text-muted fs-7 mb-1">{label}</div>
      <div className={`fw-bold fs-4 ${factice ? 'text-gray-500 fst-italic' : 'text-gray-800'}`}>
        {value}
      </div>
    </div>
  </div>
);

// Graphe de latence (ping) calqué sur GraphPopoverContent de /services/monitoring.
// Le backend renvoie une image PNG en base64 ; bu_id correspond ici à l'id du lien
// de monitoring (monitoring.id). Affiché uniquement si le graphe est disponible.
interface LatenceGraphProps {
  linkId: string;
}

const LatenceGraph = ({linkId}: LatenceGraphProps): JSX.Element | null => {
  const intl = useIntl();
  // Période fixe : dernières 24 heures.
  const {data, isLoading, isError} = useGetGraph(
    linkId,
    'ping-link',
    DEFAULT_GRAPH_PERIOD_HOURS,
    true
  );

  // Graphe indisponible (hors chargement/erreur) : ne rien afficher.
  if (!isLoading && !isError && !data?.base64) return null;

  return (
    <div>
      <div className="text-muted fs-7 fw-bold text-uppercase mb-3">
        {intl.formatMessage({id: 'LIEN.DETAIL.LATENCE.TITLE'})}
      </div>
      {isLoading && (
        <div className="text-center p-4">
          <span className="spinner-border spinner-border-sm text-primary" role="status" />
        </div>
      )}
      {isError && (
        <div className="text-center p-4 text-danger fs-7">
          {intl.formatMessage({id: 'LIEN.DETAIL.LATENCE.ERROR'})}
        </div>
      )}
      {!isLoading && !isError && data?.base64 && (
        <img
          src={`data:image/png;base64,${data.base64}`}
          alt={intl.formatMessage({id: 'LIEN.DETAIL.LATENCE.ALT'})}
          className="w-100"
        />
      )}
    </div>
  );
};

// Carte "Consommation LTE" : reprend le contenu de /services/consommationLTE.
// Affichée uniquement pour les liens 4G (is4g).
// Le cisId du lien correspond ici au serviceId (même identifiant côté CIS).
interface ConsommationLTECardProps {
  cisId: string;
}

const formatDataSize = (value: number, unit: string): {value: string; unit: string} => {
  const valueInBytes = unit.toLowerCase() === 'gb' ? value * 1024 : value;
  if (valueInBytes >= 1024) {
    return {value: (valueInBytes / 1024).toFixed(2), unit: 'GB'};
  }
  return {value: valueInBytes.toFixed(2), unit: 'MB'};
};

const ConsommationLTECard = ({cisId}: ConsommationLTECardProps): JSX.Element | null => {
  const intl = useIntl();
  const {data, isLoading, isError} = useGetConsommation(cisId);

  if (isLoading) {
    return (
      <div className="card mt-5">
        <div className="card-body d-flex justify-content-center align-items-center py-10">
          <span className="spinner-border spinner-border-sm text-primary me-3" role="status" />
          <span className="text-muted">{intl.formatMessage({id: 'LIEN.DETAIL.LTE.LOADING'})}</span>
        </div>
      </div>
    );
  }

  if (isError || !data) return null;

  const {current = 0, limit = 0, unit = ''} = data;
  const percentage = limit > 0 ? (current / limit) * 100 : 0;

  let variant = 'success';
  let completionRate = limit > 0 ? Math.round((100 * current) / limit) : 0;
  if (completionRate >= 75 && completionRate < 90) {
    variant = 'info';
  }
  if (completionRate >= 90 && completionRate < 99) {
    variant = 'warning';
  }
  if (completionRate >= 99) {
    variant = 'danger';
  }
  const rate = Math.min(100, completionRate);

  const currentFormatted = formatDataSize(current, unit);
  const limitFormatted = formatDataSize(limit, unit);

  return (
    <div className="card mt-5">
      <div className="card-header border-0 pt-6">
        <div className="card-title">
          <Level3Title title={intl.formatMessage({id: 'LIEN.DETAIL.LTE.TITLE'})} />
        </div>
      </div>
      <div className="card-body pt-2">
        <div className="row mb-5">
          <StatBox
            label={intl.formatMessage({id: 'LIEN.DETAIL.LTE.CURRENT'})}
            value={`${currentFormatted.value} ${currentFormatted.unit}`}
          />
          <StatBox
            label={intl.formatMessage({id: 'LIEN.DETAIL.LTE.LIMIT'})}
            value={`${limitFormatted.value} ${limitFormatted.unit}`}
          />
          <StatBox
            label={intl.formatMessage({id: 'LIEN.DETAIL.LTE.PERCENTAGE'})}
            value={`${percentage.toFixed(2)} %`}
          />
        </div>
        <ProgressBar now={rate} variant={variant} />
        <div className="d-flex justify-content-between text-muted fs-7 mt-2">
          <span>
            {currentFormatted.value} {currentFormatted.unit}
          </span>
          <span>
            {limitFormatted.value} {limitFormatted.unit}
          </span>
        </div>
      </div>
    </div>
  );
};

const formatAdresse = (lien?: ListService): string => {
  const site = lien?.site;
  if (!site) return '—';
  return [site.address, [site.zipCode, site.city].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
};

// Le nom du site embarque parfois déjà l'adresse : on évite alors de la répéter.
const formatSiteLivraison = (lien?: ListService): string => {
  const site = lien?.site;
  const company = site?.company?.trim();
  const adresse = formatAdresse(lien);
  const companyContientAdresse =
    !!company &&
    !!site?.zipCode &&
    company.toLowerCase().includes(site.zipCode.toLowerCase());
  return (
    [company, companyContientAdresse ? '' : adresse]
      .filter((part) => part && part !== '—')
      .join(' - ') || '—'
  );
};

const LienDetailPage = (): JSX.Element => {
  const intl = useIntl();
  const lang = useLang();
  const navigate = useNavigate();
  const {serviceId} = useParams<{serviceId: string}>();

  const {data: lien, isLoading, isError, error} = useGetServiceInfo(serviceId ?? '');
  const factice = donneesTechniquesFactices;

  const typeRouter = lien?.router?.type ?? null;
  const idRouter = lien?.router?.data?.cis_id ?? null;
  const {data: routerCapabilities} = useGetRouterCapabilities(
    typeRouter === 'Microtik' ? idRouter ?? undefined : undefined
  );
  const configurationImpossible = routerCapabilities?.autoCorrectionEnabled === false;

  const etatLienMap: Record<EtatLien, {label: string; color: string}> = {
    CONNECTED: {label: intl.formatMessage({id: 'LIEN.DETAIL.STATE.CONNECTED'}), color: '#50cd89'},
    DISCONNECTED: {label: intl.formatMessage({id: 'LIEN.DETAIL.STATE.DISCONNECTED'}), color: '#f1416c'},
    TO_CONFIGURE: {label: intl.formatMessage({id: 'LIEN.DETAIL.STATE.TO_CONFIGURE'}), color: '#a1a5b7'},
  };

  const RetourButton = (): JSX.Element => (
    <button
      type="button"
      className="btn btn-sm btn-outline btn-outline-primary btn-active-light-primary d-inline-flex align-items-center mb-5"
      onClick={() => navigate('/services/connexion/liens')}
    >
      <i className="bi bi-arrow-left me-2" />
      {intl.formatMessage({id: 'LIEN.BACK'})}
    </button>
  );

  if (isLoading) {
    return (
      <>
        <PageTitle breadcrumbs={[]}>{intl.formatMessage({id: 'MENU.CONNECTIONS.LINKS'})}</PageTitle>
        <RetourButton />
        <div className="card">
          <div className="card-body d-flex justify-content-center align-items-center py-20">
            <span className="spinner-border text-primary me-3" role="status" />
            <span className="text-muted">{intl.formatMessage({id: 'LIEN.DETAIL.LOADING'})}</span>
          </div>
        </div>
      </>
    );
  }

  if (isNotFoundError(error)) {
    return <NotFoundRedirect />;
  }

  if (isError || !lien) {
    return (
      <>
        <PageTitle breadcrumbs={[]}>{intl.formatMessage({id: 'MENU.CONNECTIONS.LINKS'})}</PageTitle>
        <RetourButton />
        <div className="card">
          <div className="card-body py-10 text-center text-muted">
            <i className="bi bi-exclamation-triangle fs-2x text-warning d-block mb-3" />
            {intl.formatMessage({id: 'LIEN.DETAIL.ERROR'}, {serviceId})}
          </div>
        </div>
      </>
    );
  }

  const monitoring = lien.monitoring;
  const miseEnProduction = lien.deliveryDate
    ? DateTime.fromISO(lien.deliveryDate).setLocale(lang).toFormat('d LLLL yyyy')
    : null;
  const etat = (monitoring?.etat as EtatLien | undefined) ?? factice.etatLien;
  const etatInfo = etatLienMap[etat];
  const enLigne = etat === 'CONNECTED';
  const router = lien.router;
  const routerData = router?.data;

  const demanderConfigRouteur = () => {
    // Le formulaire support pré-remplit automatiquement type=Technique + motif=Demande de
    // configuration dès que le query param ?routerId est présent (cf. Reclamation.tsx),
    // puis ?site et ?service pré-remplissent les champs Site et Service.
    const params = new URLSearchParams();
    params.append('routerId', idRouter ?? '-1');
    if (lien.site?.id) params.append('site', lien.site.id.toString());
    if (lien.serviceId) params.append('service', lien.serviceId.toString());
    navigate({pathname: '/support', search: params.toString()});
  };

  const ouvrirTicket = () => {
    const params = new URLSearchParams();
    if (lien.site?.id) params.append('site', lien.site.id.toString());
    if (lien.serviceId) params.append('service', lien.serviceId.toString());
    const search = params.toString() ? `?${params.toString()}` : '';
    navigate(`/support${search}`, {
      state: {
        reclamationData: {
          type: ReclamationTypes.TECHNICAL,
          motif: ReclamationReason.DEGRADATION_OF_SERVICE,
          objet: `${lien.serviceName || lien.link_name} (${lien.serviceId})`,
        },
      },
    });
  };

  return (
    <>
      <PageTitle breadcrumbs={[]}>{intl.formatMessage({id: 'MENU.CONNECTIONS.LINKS'})}</PageTitle>

      <RetourButton />

      {/* En-tête du lien */}
      <div className="card mb-5">
        <div className="card-body d-flex flex-wrap align-items-start justify-content-between gap-4">
          <div className="d-flex align-items-start">
            <div className="symbol symbol-50px me-4">
              <span className="symbol-label bg-light-primary">
                <i className="bi bi-diagram-3 fs-2 text-primary" />
              </span>
            </div>
            <div>
              <h2 className="fs-2 fw-bold text-gray-900 mb-1">
                {lien.serviceName || lien.link_name}
              </h2>
              <div className="text-muted fs-6 mb-3">
                {lien.serviceId} · {lien.buName} · {formatAdresse(lien)}
              </div>
              <div className="d-flex flex-wrap gap-2">
                <EtatBadge
                  color={getStatusColor(lien.state)}
                  label={getStatusLabel(lien.state, intl) || '-'}
                />
              </div>
            </div>
          </div>

          <button
            type="button"
            className="btn btn-sm btn-primary d-inline-flex align-items-center"
            onClick={ouvrirTicket}
          >
            <i className="bi bi-chat-left-text me-2" />
            {intl.formatMessage({id: 'SUPPORT.NEW_REQUEST'})}
          </button>
        </div>
      </div>

      <div className="row g-5">
        {/* Informations du lien */}
        <div className="col-lg-7">
          <div className="card h-100">
            <div className="card-header border-0 pt-6">
              <div className="card-title">
                <Level3Title title={intl.formatMessage({id: 'LIEN.DETAIL.INFO.TITLE'})} />
              </div>
            </div>
            <div className="card-body pt-2">
              <div className="row">
                <InfoItem label={intl.formatMessage({id: 'LIEN.DETAIL.INFO.ID'})} value={String(lien.serviceId)} />
                <InfoItem label={intl.formatMessage({id: 'LIEN.DETAIL.INFO.CONNECTION_TYPE'})} value={lien.type?.join(', ') || '—'} />
                <InfoItem
                  label={intl.formatMessage({id: 'LIEN.DETAIL.INFO.SPEED'})}
                  value={monitoring?.debit || factice.debit}
                  factice={!monitoring?.debit}
                />
                <InfoItem
                  label={intl.formatMessage({id: 'LIEN.DETAIL.INFO.IP'})}
                  value={monitoring?.ip || factice.ip}
                  factice={!monitoring?.ip}
                />
                <InfoItem label={intl.formatMessage({id: 'LIEN.DETAIL.INFO.PRODUCTION_DATE'})} value={miseEnProduction || 'N/A'} />
                <InfoItem
                  label={intl.formatMessage({id: 'LIEN.DETAIL.INFO.DELIVERY_SITE'})}
                  fullWidth
                  value={formatSiteLivraison(lien)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* État opérationnel */}
        <div className="col-lg-5">
          <div className="card h-100">
            <div className="card-header border-0 pt-6">
              <div className="card-title">
                <Level3Title title={intl.formatMessage({id: 'LIEN.DETAIL.STATE.TITLE'})} />
              </div>
            </div>
            <div className="card-body pt-2">
              <div
                className={`d-flex align-items-center rounded p-4 mb-5 ${
                  enLigne ? 'bg-light-success' : 'bg-light-danger'
                }`}
              >
                <span
                  className="rounded-circle me-3"
                  style={{width: 10, height: 10, backgroundColor: etatInfo.color, flexShrink: 0}}
                />
                <div className="d-flex flex-column">
                  <span className="fw-bold fs-5 text-gray-800">{etatInfo.label}</span>
                </div>
              </div>

              <div className="row g-3 mb-5">
                <StatBox
                  label={intl.formatMessage({id: 'LIEN.DETAIL.STATE.LATENCY'})}
                  value={monitoring?.latence_ping || factice.latence}
                  factice={!monitoring?.latence_ping}
                />
                <StatBox label={intl.formatMessage({id: 'LIEN.DETAIL.STATE.INBOUND'})} value={monitoring?.trafic_entrant || '-'} />
                <StatBox label={intl.formatMessage({id: 'LIEN.DETAIL.STATE.OUTBOUND'})} value={monitoring?.trafic_sortant || '-'} />
              </div>

              {monitoring?.ping_graph && monitoring?.id && (
                <LatenceGraph linkId={monitoring.id} />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Paramètres de connexion (login/mot de passe/prise) : renvoyés uniquement pour certains providers (ex: SWISSCOM) */}
      {lien.setting && (
        <div className="card mt-5">
          <div className="card-header border-0 pt-6">
            <div className="card-title">
              <Level3Title title={intl.formatMessage({id: 'LIEN.DETAIL.SETTINGS.TITLE'})} />
            </div>
          </div>
          <div className="card-body pt-2">
            <div className="row">
              <InfoItem
                label={intl.formatMessage({id: 'LIEN.DETAIL.SETTINGS.LOGIN_PRIMARY'})}
                value={lien.setting.login_primary || '—'}
              />
              <InfoItem
                label={intl.formatMessage({id: 'LIEN.DETAIL.SETTINGS.PASSWORD_PRIMARY'})}
                value={lien.setting.password_primary || '—'}
              />
              <InfoItem
                label={intl.formatMessage({id: 'LIEN.DETAIL.SETTINGS.TYPE_PRIMARY'})}
                value={lien.setting.type_primary || '—'}
              />
              {lien.setting.login_secondary && (
                <InfoItem
                  label={intl.formatMessage({id: 'LIEN.DETAIL.SETTINGS.LOGIN_SECONDARY'})}
                  value={lien.setting.login_secondary}
                />
              )}
              {lien.setting.password_secondary && (
                <InfoItem
                  label={intl.formatMessage({id: 'LIEN.DETAIL.SETTINGS.PASSWORD_SECONDARY'})}
                  value={lien.setting.password_secondary}
                />
              )}
              {lien.setting.type_secondary && (
                <InfoItem
                  label={intl.formatMessage({id: 'LIEN.DETAIL.SETTINGS.TYPE_SECONDARY'})}
                  value={lien.setting.type_secondary}
                />
              )}
              <InfoItem
                label={intl.formatMessage({id: 'LIEN.DETAIL.SETTINGS.SOCKET_ID'})}
                value={lien.setting.socket_id || '—'}
              />
              <InfoItem
                label={intl.formatMessage({id: 'LIEN.DETAIL.SETTINGS.PLUG_NUMBER'})}
                value={lien.setting.plug_number || '—'}
              />
              <InfoItem
                label={intl.formatMessage({id: 'LIEN.DETAIL.SETTINGS.ACTIVATED_AT'})}
                value={
                  lien.setting.activated_at
                    ? DateTime.fromSQL(lien.setting.activated_at).setLocale(lang).toFormat('d LLLL yyyy')
                    : '—'
                }
              />
              <InfoItem
                label={intl.formatMessage({id: 'LIEN.DETAIL.SETTINGS.ADDRESS'})}
                fullWidth
                value={lien.setting.address || '—'}
              />
            </div>
          </div>
        </div>
      )}

      {/* Consommation LTE : provider provient du endpoint détail (/api/services/{id}) */}
      {lien.is4g && <ConsommationLTECard cisId={String(lien.serviceId)} />}

      {/* Routeur associé */}
      <div className="card mt-5">
          <div className="card-header border-0 pt-6">
            <div className="card-title">
              <Level3Title title={intl.formatMessage({id: 'LIEN.DETAIL.ROUTER.TITLE'})} />
            </div>
          </div>
        <div className="card-body pt-2">
          <div className="d-flex align-items-center mb-6">
            <div className="symbol symbol-50px me-4">
              <span className="symbol-label bg-light-primary">
                <i className="bi bi-router fs-2 text-primary" />
              </span>
            </div>
            <div className="flex-grow-1">
              <div className="fw-bold fs-4 text-gray-900">
                {routerData?.libelle || lien.router?.type || factice.routeur.libelle}
              </div>
            </div>
          </div>

          <div className="row mb-6">
            <InfoItem label={intl.formatMessage({id: 'LIEN.DETAIL.ROUTER.SERIAL'})} value={routerData?.serial || 'N/A'} />
            <InfoItem label={intl.formatMessage({id: 'LIEN.DETAIL.ROUTER.MAC'})} value={routerData?.mac || 'N/A'} />
          </div>

          <div className="d-flex flex-wrap gap-3">
            {typeRouter === 'N/A' && (
              <button
                type="button"
                className="btn btn-sm btn-primary d-inline-flex align-items-center"
                onClick={demanderConfigRouteur}
              >
                <i className="bi bi-gear me-2" />
                {intl.formatMessage({id: 'LIEN.DETAIL.ROUTER.REQUEST_CONFIG'})}
              </button>
            )}

            {typeRouter === 'Microtik' && idRouter !== null && (
              <OverlayTrigger
                overlay={
                  <Tooltip id="router-configure-tooltip">
                    {intl.formatMessage({id: 'LIEN.DETAIL.ROUTER.CONFIGURE.IMPOSSIBLE'})}
                  </Tooltip>
                }
                show={configurationImpossible ? undefined : false}
              >
                <span className="d-inline-block">
                  <button
                    type="button"
                    className={`btn btn-sm d-inline-flex align-items-center ${
                      configurationImpossible ? 'btn-secondary' : 'btn-primary'
                    }`}
                    disabled={configurationImpossible}
                    onClick={() => navigate(`/configuration/routeurs/${idRouter}`)}
                    style={configurationImpossible ? {pointerEvents: 'none'} : undefined}
                  >
                    <i className="fs-1 fa-solid fa-gears" />
                    {intl.formatMessage({id: 'LIEN.DETAIL.ROUTER.CONFIGURE'})}
                  </button>
                </span>
              </OverlayTrigger>
            )}

          </div>
          </div>
        </div>
    </>
  );
};

export default LienDetailPage;
